import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Package, Wallet, ClipboardList, Columns } from 'lucide-react';
import StaffLayout from '../../layouts/StaffLayout';
import { ordersApi, cashDrawerApi } from '../../services/api';
import { formatCurrency, StatusBadge, PaymentBadge } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';

export default function StaffDashboardPage() {
  const { lang, t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: shift } = useQuery({
    queryKey: ['staff-cash-shift'],
    queryFn: () => cashDrawerApi.getCurrent().then(r => r.data),
    refetchInterval: 30000,
  });

  const { data: ordersData, isLoading } = useQuery({
    queryKey: ['staff-orders-recent'],
    queryFn: () => ordersApi.list({ limit: 5, my_shift_only: false }).then(r => r.data),
    refetchInterval: 15000,
  });

  const quickActions = [
    { label: t('Buat Pesanan', 'New Order'), path: '/karyawan/order-create', icon: Plus, color: 'bg-[#0f6c44]' },
    { label: 'Kanban', path: '/karyawan/order-board', icon: Columns, color: 'bg-blue-500' },
    { label: t('Daftar Pesanan', 'Orders'), path: '/karyawan/orders', icon: ClipboardList, color: 'bg-purple-500' },
    { label: t('Laci Kas', 'Cash Drawer'), path: '/karyawan/cash-drawer', icon: Wallet, color: 'bg-orange-500' },
  ];

  return (
    <StaffLayout>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900 font-manrope">{t('Selamat datang', 'Welcome')}, {user?.name?.split(' ')[0]}!</h1>
            <p className="text-slate-500 text-sm mt-0.5">
              {shift ? (
                <span className="flex items-center gap-1.5 text-emerald-600 font-medium">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                  {t('Shift sedang aktif', 'Shift is active')}
                </span>
              ) : (
                <span className="text-orange-500">{t('Shift belum dibuka', 'Shift not opened')}</span>
              )}
            </p>
          </div>
          <button onClick={() => navigate('/karyawan/order-create')}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#0f6c44] text-white rounded-xl text-sm font-semibold hover:bg-[#0c5636] active:scale-95 transition-all shadow-sm">
            <Plus size={18} />{t('Buat Pesanan', 'New Order')}
          </button>
        </div>

        {/* Shift warning */}
        {!shift && (
          <div className="bg-orange-50 border border-orange-100 rounded-2xl p-4 flex items-center justify-between">
            <div>
              <p className="font-semibold text-orange-700 text-sm">{t('Shift belum dibuka!', 'Shift not started!')}</p>
              <p className="text-orange-500 text-xs mt-0.5">{t('Buka shift dahulu sebelum mulai transaksi', 'Open a shift before starting transactions')}</p>
            </div>
            <button onClick={() => navigate('/karyawan/cash-drawer')}
              className="px-4 py-2 bg-orange-500 text-white rounded-xl text-xs font-semibold hover:bg-orange-600 transition-colors">
              {t('Buka Shift', 'Open Shift')}
            </button>
          </div>
        )}

        {/* Kas info if shift active */}
        {shift && (
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: t('Modal Awal', 'Opening'), value: shift.opening_cash },
              { label: t('Kas Masuk', 'Cash In'), value: shift.cash_received || 0 },
              { label: t('Ekspektasi', 'Expected'), value: shift.expected_cash || 0 },
            ].map(item => (
              <div key={item.label} className="bg-white rounded-2xl border border-slate-100 p-4 text-center shadow-sm">
                <p className="text-xs text-slate-400 mb-1">{item.label}</p>
                <p className="text-sm font-bold text-slate-800 font-manrope">{formatCurrency(item.value)}</p>
              </div>
            ))}
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {quickActions.map(a => (
            <button key={a.path} onClick={() => navigate(a.path)}
              className="bg-white rounded-2xl border border-slate-100 p-4 flex flex-col items-center gap-3 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 shadow-sm">
              <div className={`w-11 h-11 ${a.color} rounded-xl flex items-center justify-center`}>
                <a.icon size={20} className="text-white" />
              </div>
              <span className="text-xs font-semibold text-slate-700 text-center">{a.label}</span>
            </button>
          ))}
        </div>

        {/* Recent orders */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-50">
            <h3 className="font-semibold text-slate-800 font-manrope">{t('Pesanan Terbaru', 'Recent Orders')}</h3>
            <button onClick={() => navigate('/karyawan/orders')} className="text-xs text-[#0f6c44] font-semibold hover:underline">
              {t('Lihat semua', 'See all')}
            </button>
          </div>
          <div className="divide-y divide-slate-50">
            {isLoading ? [1,2,3].map(i => (
              <div key={i} className="px-5 py-3.5 flex justify-between items-center">
                <div className="h-4 bg-slate-100 rounded w-32 animate-pulse" />
                <div className="h-4 bg-slate-100 rounded w-20 animate-pulse" />
              </div>
            )) : (ordersData?.items || []).length === 0 ? (
              <div className="px-5 py-8 text-center text-slate-400 text-sm">
                <Package size={24} className="mx-auto mb-2 opacity-40" />
                {t('Belum ada pesanan', 'No orders yet')}
              </div>
            ) : (ordersData?.items || []).map(order => (
              <div key={order.id} onClick={() => navigate(`/karyawan/orders/${order.id}`)}
                className="px-5 py-3.5 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs font-bold text-[#0f6c44] bg-emerald-50 px-2 py-0.5 rounded">{order.tracking_code}</span>
                  <span className="text-sm text-slate-700 font-medium truncate max-w-[120px]">{order.customer_name || '-'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <PaymentBadge status={order.payment_status} lang={lang} />
                  <StatusBadge status={order.status} lang={lang} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </StaffLayout>
  );
}
